import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/client';
import {
    Box,
    Typography,
    TextField,
    Button,
    Alert,
    InputAdornment,
    CircularProgress,
    Fade,
    Paper,
    Grid,
    IconButton
} from '@mui/material';
import { Lock, HelpCircle, UserCheck, Eye, EyeOff } from 'lucide-react';
import { BusinessCenter } from '@mui/icons-material';

const Login = () => {
    const [loginIdentifier, setLoginIdentifier] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const navigate = useNavigate();

    const getDynamicGreeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return "Good morning";
        if (hour < 17) return "Good afternoon";
        return "Good evening";
    };

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            setError('');
            setIsSubmitting(true);

            // FIX: Match the backend expected keys directly without stripping either out as undefined.
            // This ensures lookUpIdentifier = email || employeeId || null resolves correctly on the server.
            const cleanIdentifier = loginIdentifier.trim();
            const payload = {
                email: cleanIdentifier,
                employeeId: cleanIdentifier,
                password: password
            };

            const response = await API.post('/auth/login', payload);

            localStorage.setItem('token', response.data.token);
            localStorage.setItem('crm_token', response.data.token);
            localStorage.setItem('user_role', response.data.user.role);
            localStorage.setItem('user_name', response.data.user.name);
            localStorage.setItem('user', JSON.stringify(response.data.user));

            const targetRole = String(response.data.user.role || '').trim().toLowerCase();

            if (targetRole === 'admin') {
                navigate('/admin/leads');
            } else if (targetRole === 'bde' || targetRole === 'business development executive') {
                navigate('/bde/leads');
            } else if (targetRole === 'hr') {
                navigate('/hr/leads');
            } else {
                navigate('/leads');
            }

        } catch (err) {
            console.error("❌ Authentication gateway reject:", err);
            setError(err.response?.data?.message || 'Invalid system access credentials.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Grid container sx={{ minHeight: '100vh', bgcolor: '#f3f3f2', fontFamily: '"Salesforce Sans", Arial, sans-serif' }}>
            
            {/* Left Brand Identity Hero (Salesforce Deep Blue to Sky Gradient) */}
            <Grid
                item
                xs={false}
                sm={4}
                md={6}
                lg={7}
                sx={{
                    background: 'linear-gradient(135deg, #1798c1 0%, #0176d3 50%, #032d60 100%)',
                    display: { xs: 'none', sm: 'flex' },
                    flexDirection: 'column',
                    justifyContent: 'center',
                    p: 8,
                    color: '#ffffff',
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                {/* Clean Decorative Lightning/Cloud Arc Effect */}
                <Box sx={{ position: 'absolute', bottom: '-20%', left: '-10%', width: '100%', height: '60%', background: 'rgba(255, 255, 255, 0.05)', borderRadius: '50%', transform: 'rotate(-15deg)' }} />
                
                <Box sx={{ position: 'relative', maxWidth: '540px' }}>
                    <Typography variant="h3" sx={{ fontWeight: 300, mb: 3, fontSize: { sm: '2.5rem', md: '3.5rem' }, lineHeight: 1.2 }}>
                        Discover the power of <br />
                        <Box component="span" sx={{ fontWeight: 800 }}>TechnGlobal CRM</Box>
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.9, fontSize: '16px', lineHeight: 1.6, fontWeight: 400 }}>
                        Welcome to your unified workspace. Manage client pipelines, track enterprise clearances, and maximize team velocity through the official TechnGlobal cloud platform network.
                    </Typography>
                </Box>
            </Grid>

            {/* Right Dedicated Login Panel */}
            <Grid
                item
                xs={12}
                sm={8}
                md={6}
                lg={5}
                display="flex"
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
                sx={{ p: { xs: 3, md: 6 } }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        width: '100%',
                        maxWidth: '420px',
                        p: 4,
                        borderRadius: '8px',
                        border: '1px solid #aeabaf',
                        bgcolor: '#ffffff'
                    }}
                >
                    {/* Salesforce Header Logo Concept */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 4 }}>
                        <Box sx={{ bgcolor: '#0176d3', color: '#ffffff', p: 1, borderRadius: '4px', display: 'flex' }}>
                            <BusinessCenter sx={{ fontSize: '24px' }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#032d60', fontSize: '18px', letterSpacing: '-0.2px', lineHeight: 1 }}>
                                TechnGlobal
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 700, fontSize: '11px', letterSpacing: '0.5px' }}>
                                LIGHTNING CONSOLE
                            </Typography>
                        </Box>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#032d60', fontSize: '20px', mb: 0.5 }}>
                            {getDynamicGreeting()}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#514f4d', fontSize: '13px' }}>
                            Please log in with your corporate domain identity.
                        </Typography>
                    </Box>

                    {/* Alert Handling */}
                    {error && (
                        <Fade in={!!error}>
                            <Alert severity="error" sx={{ mb: 3, borderRadius: '4px', border: '1px solid #ea001e', bgcolor: '#ffeef0', color: '#ea001e', fontWeight: 600, fontSize: '13px', '.MuiAlert-icon': { color: '#ea001e' } }}>
                                {error}
                            </Alert>
                        </Fade>
                    )}

                    {/* Form Layout */}
                    <form onSubmit={handleLogin}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <TextField
                                fullWidth
                                label="Username / Employee ID"
                                variant="outlined"
                                value={loginIdentifier}
                                onChange={(e) => setLoginIdentifier(e.target.value)}
                                required
                                disabled={isSubmitting}
                                size="small"
                                InputLabelProps={{ shrink: true, sx: { fontSize: '13px', fontWeight: 700, color: '#514f4d', transform: 'translate(0, -18px) scale(1)' } }}
                                InputProps={{
                                    sx: { 
                                        borderRadius: '4px', 
                                        bgcolor: '#ffffff', 
                                        height: '36px', 
                                        fontSize: '13px', 
                                        color: '#081c3b', 
                                        '.MuiOutlinedInput-notchedOutline': { borderColor: '#aeabaf' }, 
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#747474' }, 
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0176d3', borderWidth: '1.5px' } 
                                    },
                                    startAdornment: (
                                        <InputAdornment position="start" sx={{ color: '#747474' }}>
                                            <UserCheck size={16} />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <TextField
                                fullWidth
                                type={showPassword ? 'text' : 'password'}
                                label="Password"
                                variant="outlined"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={isSubmitting}
                                size="small"
                                InputLabelProps={{ shrink: true, sx: { fontSize: '13px', fontWeight: 700, color: '#514f4d', transform: 'translate(0, -18px) scale(1)' } }}
                                InputProps={{
                                    sx: { 
                                        borderRadius: '4px', 
                                        bgcolor: '#ffffff', 
                                        height: '36px', 
                                        fontSize: '13px', 
                                        color: '#081c3b', 
                                        '.MuiOutlinedInput-notchedOutline': { borderColor: '#aeabaf' }, 
                                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#747474' }, 
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#0176d3', borderWidth: '1.5px' } 
                                    },
                                    startAdornment: (
                                        <InputAdornment position="start" sx={{ color: '#747474' }}>
                                            <Lock size={16} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={handleClickShowPassword}
                                                edge="end"
                                                sx={{ color: '#747474', p: '4px' }}
                                            >
                                                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            <Button
                                type="submit"
                                variant="contained"
                                fullWidth
                                disabled={isSubmitting}
                                sx={{
                                    bgcolor: '#0176d3',
                                    color: '#ffffff',
                                    fontWeight: 700,
                                    textTransform: 'none',
                                    height: '36px',
                                    borderRadius: '4px',
                                    fontSize: '13px',
                                    boxShadow: 'none',
                                    transition: 'background-color 0.1s linear',
                                    '&:hover': {
                                        bgcolor: '#014486',
                                        boxShadow: 'none'
                                    }
                                }}
                            >
                                {isSubmitting ? (
                                    <CircularProgress size={20} sx={{ color: '#ffffff' }} />
                                ) : (
                                    'Log In'
                                )}
                            </Button>
                        </Box>
                    </form>
                </Paper>

                {/* Sub-Form Corporate Identity Footer */}
                <Box sx={{ mt: 4, width: '100%', maxWidth: '420px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1 }}>
                    <Typography variant="caption" sx={{ color: '#444444', fontWeight: 500 }}>
                        © 2026 TechnGlobal Inc.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', color: '#0176d3', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}>
                        <HelpCircle size={13} />
                        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '12px' }}>System IT Desk</Typography>
                    </Box>
                </Box>
            </Grid>
        </Grid>
    );
};

export default Login;